import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  Breadcrumbs,
  Link,
  Button
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DealCard from '../components/DealCard';
import SEOHead, { generateVenueStructuredData, generateBreadcrumbStructuredData } from '../components/SEOHead';
import { useCityContext } from '../contexts/CityContext';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SingleVenuePage = () => {
  const { venueId } = useParams();
  const { city } = useCityContext();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        const venueDoc = await getDoc(doc(db, 'venues', venueId));
        if (venueDoc.exists()) {
          setVenue({ id: venueDoc.id, ...venueDoc.data() });
        } else {
          setError('Venue not found');
        }
      } catch (err) {
        console.error('Error fetching venue:', err);
        setError('Failed to load venue');
      } finally {
        setLoading(false);
      }
    };

    fetchVenue();
  }, [venueId]);

  // Generate SEO data
  const getSEOData = () => {
    if (!venue) return {};

    const cityName = city?.name || 'Tel Aviv';
    const siteUrl = city?.siteUrl || window.location.origin;
    const title = `${venue.headline} - Happy Hour Deals | ${city?.displayName || 'Happy Hours'}`;
    const description = venue.main_offer
      ? `${venue.main_offer} at ${venue.headline}, ${venue.address}. Find happy hour deals and special offers in ${cityName}.`
      : `Happy hour deals at ${venue.headline} in ${venue.address}. Discover great drink specials and offers in ${cityName}.`;

    // Generate breadcrumb schema
    const breadcrumbItems = [
      { name: city?.displayName || 'Home', url: '/' },
      { name: venue.headline }
    ];

    // Combine venue schema and breadcrumb schema
    const structuredData = [
      generateVenueStructuredData(venue, city),
      generateBreadcrumbStructuredData(breadcrumbItems, city)
    ].filter(Boolean);

    return {
      title,
      description,
      canonicalUrl: `${siteUrl}/venue/${venueId}`,
      ogImage: venue.image_url && venue.image_url !== 'none' ? venue.image_url : undefined,
      ogType: 'restaurant.restaurant',
      structuredData
    };
  };

  const seoData = getSEOData();

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (error || !venue) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
        <SEOHead
          title={`Venue Not Found | ${city?.displayName || 'Happy Hours'}`}
          description="The venue you're looking for could not be found."
          noIndex={true}
        />
        <Header />
        <Container maxWidth="sm" sx={{ mt: 4, flexGrow: 1, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error || 'Venue not found'}
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Back to All Venues
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
      <SEOHead {...seoData} />
      <Header />

      <Container maxWidth="sm" sx={{ mt: 3, mb: 4, flexGrow: 1 }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            {city?.displayName || 'Home'}
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 500 }}>
            {venue.headline}
          </Typography>
        </Breadcrumbs>

        {/* Back button for mobile */}
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBackIcon />}
          sx={{
            mb: 2,
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'rgba(217, 119, 6, 0.08)'
            }
          }}
        >
          Back to all deals
        </Button>

        {/* Venue Card */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DealCard venue={venue} />
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default SingleVenuePage;
